import React, { useState, useContext, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { XCSS } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AutomationIcon from '@atlaskit/icon/core/automation';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';

import { getAutomationStack } from './utils/AutomationAPIUtilities';
import { EventSourceName } from './utils/analytics';
import { InContextRuleBuilderContext } from './InContextRuleBuilderContext';
import { commonI18n } from './i18n';
import { useExperimentalCreateAutomationRuleFromTemplateMutation } from './useExperimentalCreateAutomationRuleFromTemplateMutation';

const insightMessageStyles = xcss({ marginLeft: 'space.300' });
const insightMessageInContextStyles = xcss({ marginLeft: 'space.300', marginTop: 'space.050' });
export const i18n = defineMessages({
	buildingRule: {
		id: 'automation-discovery.common.building-rule',
		defaultMessage: 'Building rule',
		description: 'Label to be shown while the rule visual animation is building',
	},
	createRule: {
		id: 'automation-discovery.common.create-rule',
		defaultMessage: 'Turn on rule',
		description: 'This is a call to action button that will create a new automation rule',
	},
	createRuleSuccess: {
		id: 'automation-discovery.common.create-rule-success',
		defaultMessage: 'Rule has been turned on',
		description: 'Text that is shown after an automation rule is successfully created',
	},
	viewRule: {
		id: 'automation-discovery.common.view-rule',
		defaultMessage: 'View rule',
		description: 'Label for user to view their newly created rule in the rule builder',
	},
	editInRuleBuilder: {
		id: 'automation-discovery.common.edit-in-rule-builder',
		defaultMessage: 'Edit in rule builder',
		description: 'Label for user to link them to the rule preview in the rule builder',
	},
	errorFlagTitle: {
		id: 'automation-discovery.common.error-flag.title',
		defaultMessage: 'Failed to create rule',
		description: 'Title on error flag telling the user about the unexpected failure',
	},
	errorFlagDescription: {
		id: 'automation-discovery.common.error-flag.description',
		defaultMessage: 'An unexpected error occurred while creating the rule.',
		description: 'Description on the error flag.',
	},
});

const ConditionalWrapper = ({ condition, WrapperIfTrue, WrapperIfFalse, children }) =>
	condition ? (
		<WrapperIfTrue>{children}</WrapperIfTrue>
	) : (
		<WrapperIfFalse>{children}</WrapperIfFalse>
	);

const StackWrapper = ({ children }) => <Stack>{children}</Stack>;
const InlineWrapper = ({ children }) => (
	<Inline space="space.100" shouldWrap>
		{children}
	</Inline>
);

const defaultInContextPromptContainerStyles = xcss({
	padding: 'space.150',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	marginBottom: 'space.200',
	marginRight: 'space.0',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BouncingDot = styled.div({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'@keyframes bouncing': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'0%': {
			transform: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'33%': {
			transform: 'translateY(-.3em)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'66%': {
			transform: 'none',
		},
	},
	animationName: 'bouncing',
	animationDuration: '900ms',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'ease-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-child(2)': {
		animationDelay: '150ms',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-child(3)': {
		animationDelay: '300ms',
	},
});

export interface InContextRuleBuilderProps {
	templateId: string;
	templateDescription: React.ReactNode; // FormattedMessage describing the template
	spaceKey: string | null | undefined;
	message: React.ReactNode; // FormattedMessage describing the prompt
	insightMessage?: React.ReactNode;
	handleRedirectToRuleBuilder: () => void; // Function to be called when the user clicks "Edit in rule builder"
	handleDismiss: () => void; // Function to be called when the user clicks "Not now"
	handleRuleCreationStart?: () => void; // Function to be called before one-click rule creation
	handleRuleCreationSuccess: () => void; // Function to be called when the one-click rule creation succeeds
	handleRuleCreationFail?: (apolloError: ApolloErrorInterop) => void;
	flags: FlagsStateContainer;
	children: React.ReactNode;
	automationDiscoveryParent: EventSourceName;
	customContainerStyles?: XCSS;
	checkIsInContextRuleBuilderStateValid: () => boolean;
	ruleComponentAlignment?: 'inline' | 'stack';
	displayMode?: 'in-context' | 'modal';
	disableCreateRule?: boolean;
}

const InContextRuleBuilder = ({
	templateId,
	spaceKey,
	templateDescription,
	handleRedirectToRuleBuilder,
	handleRuleCreationStart,
	handleRuleCreationSuccess,
	flags,
	children,
	automationDiscoveryParent,
	customContainerStyles,
	checkIsInContextRuleBuilderStateValid,
	ruleComponentAlignment = 'stack',
	displayMode = 'in-context',
	handleRuleCreationFail,
	disableCreateRule,
	handleDismiss,
	message,
	insightMessage,
}: InContextRuleBuilderProps) => {
	const { cloudId, environment } = useSessionData();
	const { templateApiParameters } = useContext(InContextRuleBuilderContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(spaceKey || undefined);
	const [createdRuleId, setCreatedRuleId] = useState<string | null>(null);
	const [displayPrompt, setShouldDisplayPrompt] = useState(true);
	const [animationState, setAnimationState] = useState<number>(-1);
	const timeoutID = useRef<any>(null);
	const isAnimationFinished = animationState >= React.Children.count(children) + 1;

	const onCompleted = (result) => {
		if (result?.experimentalCreateAutomationRuleFromTemplate?.ruleId) {
			handleRuleCreationSuccess();
			setCreatedRuleId(result.experimentalCreateAutomationRuleFromTemplate.ruleId);
		}
	};

	const onError = (err) => {
		handleRuleCreationFail?.(err);
		void flags.showErrorFlag({
			title: <FormattedMessage {...i18n.errorFlagTitle} />,
			description: <FormattedMessage {...i18n.errorFlagDescription} />,
			isAutoDismiss: true,
		});
	};

	const [createAutomationRuleFromTemplate, { data, loading, error: mutationError }] =
		useExperimentalCreateAutomationRuleFromTemplateMutation({
			onCompleted,
			onError,
		});

	useEffect(() => {
		if (isAnimationFinished) {
			clearTimeout(timeoutID.current);
			timeoutID.current = null;
			return;
		}

		// Count up to the # of components in the rule we want to render
		if (!displayPrompt) {
			timeoutID.current = setTimeout(() => {
				setAnimationState((value) => value + 1);
			}, 1000);
		}

		return () => clearTimeout(timeoutID.current);
	}, [animationState, isAnimationFinished, displayPrompt]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: EventSourceName.IN_CONTEXT_RULE_BUILDER,
				source: EventSourceName.IN_CONTEXT_RULE_BUILDER,
				attributes: {
					automationDiscoveryParent,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, automationDiscoveryParent]);

	const handleOnClickCreateRule = async () => {
		if (checkIsInContextRuleBuilderStateValid()) {
			handleRuleCreationStart?.();
			// This is really fast in prod (no remote call)
			const automationStack = await getAutomationStack(environment);
			const formattedParameters = Object.entries(templateApiParameters).map(
				([parameterName, parameterValue]) => ({
					name: parameterName,
					type: parameterValue.type,
					value: String(parameterValue.value),
				}),
			);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'automationDiscoveryInContextRuleBuilderTurnOn',
					source: EventSourceName.IN_CONTEXT_RULE_BUILDER,
					attributes: {
						spaceKey,
						automationDiscoveryParent,
					},
				},
			}).fire();

			await createAutomationRuleFromTemplate({
				variables: {
					templateId,
					cloudId,
					automationStack,
					spaceId,
					parameters: formattedParameters,
				},
			});
		}
	};

	const renderInContextActionButtons = () => {
		if (data) {
			return (
				<>
					<Button
						appearance="link"
						href={`/wiki/spaces/${spaceKey}/settings/automation#/rule/${createdRuleId}`}
						target="_blank"
						spacing="none"
					>
						<FormattedMessage {...i18n.viewRule} />
					</Button>
					<Inline
						alignBlock="center"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
						xcss={xcss({
							color: 'color.text.success',
						})}
					>
						<CheckMarkIcon label="" color={token('color.icon.accent.green')} size="small" />
						<FormattedMessage {...i18n.createRuleSuccess} />
					</Inline>
				</>
			);
		}

		return (
			<>
				<Button
					appearance={disableCreateRule ? 'primary' : 'link'}
					onClick={() => {
						handleRedirectToRuleBuilder();
					}}
					spacing={disableCreateRule ? 'compact' : 'none'}
				>
					<FormattedMessage {...i18n.editInRuleBuilder} />
				</Button>
				{!disableCreateRule ? (
					<LoadingButton
						appearance="primary"
						spacing="compact"
						isLoading={loading}
						onClick={handleOnClickCreateRule}
					>
						<FormattedMessage {...i18n.createRule} />
					</LoadingButton>
				) : null}
			</>
		);
	};

	if (displayPrompt) {
		return displayMode === 'modal' ? (
			<Stack
				grow="fill"
				space="space.150"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					padding: 'space.150',
					backgroundColor: 'color.background.accent.gray.subtlest',
				})}
			>
				<Inline alignBlock="center" space="space.100">
					<AutomationIcon label="" color={token('color.icon.accent.blue')} />
					<Heading size="xsmall">{message}</Heading>
				</Inline>
				{insightMessage && <Box xcss={insightMessageStyles}>{insightMessage}</Box>}
				<Inline alignBlock="center" space="space.200" alignInline="end">
					<Button appearance="link" onClick={handleDismiss} spacing="none">
						<FormattedMessage {...commonI18n.dismiss} />
					</Button>
					<Button appearance="link" onClick={() => setShouldDisplayPrompt(false)} spacing="none">
						<FormattedMessage {...commonI18n.showMeHow} />
					</Button>
				</Inline>
			</Stack>
		) : (
			<Box
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					borderColor: 'color.border',
					borderStyle: 'solid',
					borderRadius: 'radius.large',
					borderWidth: 'border.width',
					backgroundColor: 'color.background.neutral',
					padding: 'space.200',
				})}
			>
				<Inline spread="space-between" alignBlock="center">
					<Inline space="space.100" alignBlock="center">
						<AutomationIcon label="" color={token('color.icon.accent.blue')} />
						<Heading size="xsmall">{message}</Heading>
					</Inline>
					<Inline space="space.100" alignBlock="center">
						<Button appearance="subtle-link" onClick={handleDismiss}>
							<FormattedMessage {...commonI18n.dismiss} />
						</Button>
						<Button
							appearance="link"
							onClick={() => setShouldDisplayPrompt(false)}
							spacing="compact"
						>
							<FormattedMessage {...commonI18n.showMeHow} />
						</Button>
					</Inline>
				</Inline>
				{insightMessage && <Box xcss={insightMessageInContextStyles}>{insightMessage}</Box>}
			</Box>
		);
	}

	return (
		<Inline
			grow="fill"
			space="space.150"
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
			xcss={[defaultInContextPromptContainerStyles, customContainerStyles]}
		>
			<AutomationIcon label="" color={token('color.icon.accent.blue')} />
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
			<Stack space="space.200" xcss={xcss({ width: '100%' })}>
				<Inline spread="space-between" space="space.200" alignBlock="center">
					{isAnimationFinished ? (
						<>
							<Heading size="xsmall">{templateDescription}</Heading>
							{displayMode === 'in-context' && (
								<Inline alignBlock="center" space="space.200">
									{renderInContextActionButtons()}
								</Inline>
							)}
						</>
					) : (
						<Heading size="xsmall">
							<FormattedMessage {...i18n.buildingRule} />
							<span>
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<BouncingDot>.</BouncingDot>
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<BouncingDot>.</BouncingDot>
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<BouncingDot>.</BouncingDot>
							</span>
						</Heading>
					)}
				</Inline>
				<ConditionalWrapper
					condition={ruleComponentAlignment === 'stack'}
					WrapperIfTrue={StackWrapper}
					WrapperIfFalse={InlineWrapper}
				>
					{React.Children.map(children, (child, componentNumber) => {
						if (React.isValidElement(child)) {
							return React.cloneElement(child as React.ReactElement<any>, {
								isSkeleton: componentNumber === animationState,
								isHidden: componentNumber > animationState,
							});
						}
						return child;
					})}
				</ConditionalWrapper>
				{!!insightMessage && isAnimationFinished && <Box>{insightMessage}</Box>}
				{isAnimationFinished && displayMode === 'modal' && (
					<Inline alignInline="end" space="space.200">
						{renderInContextActionButtons()}
					</Inline>
				)}
			</Stack>
			{mutationError && <ErrorDisplay error={mutationError} />}
		</Inline>
	);
};

export const InContextAutomationPrompt = withFlags(
	withTransparentErrorBoundary({
		attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
	})(InContextRuleBuilder),
);
